import React from 'react';
import _get from 'lodash/get';

import { PageErrorUi } from 'components';
import Loader from './Loader';
import { useSeriesFetch } from 'utils/hooks';
import { PRODUCT_WINDOW_TYPE } from '../stubs';

/**
 * @type {React.Context<{
 *  skuWindows: any[];
 *  onReload: () => void;
 * }>}
 */
const ProductReturnWindowsContext = React.createContext();

export function useProductReturnWindows() {
  const context = React.useContext(ProductReturnWindowsContext);
  if (!context) {
    throw new Error(
      'useProductReturnWindows must be used within a ProductReturnWindowsProvider'
    );
  }
  return context;
}

export function ProductReturnWindowsProvider({ children = null }) {
  const {
    data: skuWindows,
    isLoading,
    isError,
    fetchData: onReload
  } = useSeriesFetch(
    [
      {
        dataKey: 'productSkuWindows',
        apiKey: 'configure.serviceWindow.exceptions.productSkus.root',
        payload: { page_size: 10000 },
        onTransform: data => {
          const results = _get(data, 'results', []);
          return results.map(item => ({
            ...item,
            _windowType: PRODUCT_WINDOW_TYPE.SKU
          }));
        }
      },
      {
        dataKey: 'productTypeWindows',
        apiKey: 'configure.serviceWindow.exceptions.productTypes.root',
        payload: { page_size: 10000 },
        onTransform: data => {
          const results = _get(data, 'results', []);
          return results.map(item => ({
            ...item,
            _windowType: PRODUCT_WINDOW_TYPE.TYPE
          }));
        }
      }
    ],
    {
      initialData: [],
      onTransform: aggregateData => {
        const productSkuWindows = _get(aggregateData, 'productSkuWindows', []);
        const productTypeWindows = _get(
          aggregateData,
          'productTypeWindows',
          []
        );
        // Combine both lists
        return [...productSkuWindows, ...productTypeWindows];
      }
    }
  );

  return (
    <ProductReturnWindowsContext.Provider value={{ skuWindows, onReload }}>
      <PageErrorUi
        isError={isError}
        onReload={onReload}
        msg="Unable to load the SKU Return Windows at the moment!"
      >
        <Loader isLoading={isLoading}>{children}</Loader>
      </PageErrorUi>
    </ProductReturnWindowsContext.Provider>
  );
}
